import { roleApi } from '@/api/account-management/roleApi';
import { type Role, type RoleLight } from '@/models/account-management/Role';
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import TransferList, { type KeyValuePair } from "../common/transfer-list/TransferList";
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { useAppContext } from '../../AppContext';

export interface RoleTransferList {
    selected: RoleLight[] | undefined;
    setSelected: (roles: RoleLight[]) => void;
    listWidth?: number;
    listHeight?: number;
}

const RoleTransferList = ({ selected = [], setSelected, listWidth, listHeight }: RoleTransferList) => {
    const app = useAppContext();
    const { data: roleList } = useQuery<Role[]>({ queryKey: ['roles', app.organizationId], queryFn: () => roleApi.list(app.organizationId), staleTime: Infinity });
    const [unselected, setUnselected] = useState<KeyValuePair<string>[]>([]);
    const strings = useLocalizedStrings();

    useEffect(() => {
        if (!roleList) return;        
        setUnselected(roleList.map(l => ({ key: l.id, value: l.name })) as KeyValuePair<string>[]);
    }, [roleList]);

    const getLeft = () => {
        // Remove the passed in selected items from the unselected list
        return unselected.filter(l => !selected.some(role => role.id === l.key));
    }

    const getRight = () => {
        // Map the selected ids to key value pairs
        return selected.map(s => {            
            const role = roleList?.find(r => r.id === s.id);            
            return role ? { key: role.id!, value: role.name } : { key: s.id, value: strings.loading };
        });
    }

    const setRight = (items: KeyValuePair<string>[]) => {
        setSelected(items.map(i => {
            const role = roleList?.find(r => r.id === i.key);
            return role ? { id: role.id!, name: role.name } : { id: i.key, name: 'unknown' };
        }));
    }

    return (
        <TransferList 
            left={getLeft()} 
            setLeft={setUnselected}  
            right={getRight()}
            setRight={setRight}
            listWidth={listWidth}
            listHeight={listHeight}
        />
    );
}

export default RoleTransferList;